import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getMyListings, type OwnerListing } from "@/services/ownerService";
import { isPaidPlan } from "@/utils/plans";

interface UnlockItem {
  icon: string;
  title: string;
  desc: string;
  plans: string;
}

/** Everything a paid plan unlocks on a public listing. */
const UNLOCKS: UnlockItem[] = [
  {
    icon: "ri-time-line",
    title: "Live opening-hours status",
    desc: "Show customers a real-time “Open now” badge computed from your weekly hours — not a static flag.",
    plans: "Business + Premium",
  },
  {
    icon: "ri-share-forward-line",
    title: "Public share counter",
    desc: "Display a “Shared N times” total right on your listing page so your social proof is visible.",
    plans: "Business + Premium",
  },
  {
    icon: "ri-vip-crown-line",
    title: "Featured listing eligibility",
    desc: "Get considered for the featured slots and priority ranking in relevant search results.",
    plans: "Premium",
  },
];

/**
 * Owner-dashboard summary of what upgrading unlocks.
 * Shown locked while any listing is on the Free plan, and as a
 * confirmation state once every listing is on a paid plan.
 */
export default function PremiumFeaturesCard() {
  const { accessToken } = useAuth();
  const [listings, setListings] = useState<OwnerListing[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    getMyListings(accessToken)
      .then((res) => {
        if (active) setListings(res);
      })
      .catch(() => {
        /* keep empty → still show the upgrade card */
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [accessToken]);

  const freeCount = listings.filter((l) => !isPaidPlan(l.planSlug)).length;
  const paidCount = listings.length - freeCount;
  const hasPremium = listings.some((l) => l.planSlug?.toLowerCase() === "premium");
  const allPaid = listings.length > 0 && freeCount === 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center rounded-xl border border-background-200 bg-background-50 py-10">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
      </div>
    );
  }

  // ── Unlocked confirmation ──────────────────────────────────────────────────
  if (allPaid) {
    return (
      <div className="flex flex-wrap items-center gap-4 rounded-xl border border-background-200 bg-background-50 px-6 py-5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
          <i className="ri-checkbox-circle-line text-lg" />
        </span>
        <div className="min-w-0">
          <h2 className="font-heading text-sm font-bold text-foreground-950">
            Premium features unlocked
          </h2>
          <p className="mt-0.5 text-xs text-foreground-600">
            Your paid {paidCount === 1 ? "listing" : "listings"} include live hours and the public
            share counter{hasPremium ? ", plus featured listing eligibility" : ""}.
          </p>
        </div>
        <Link
          to="/listing-plans"
          className="ml-auto inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
        >
          View plans <i className="ri-arrow-right-line text-sm" />
        </Link>
      </div>
    );
  }

  // ── Locked / upgrade state ─────────────────────────────────────────────────
  const subtitle =
    freeCount > 0
      ? `${freeCount} of your ${listings.length} ${listings.length === 1 ? "listing is" : "listings are"} on the Free plan. Upgrade to unlock:`
      : "Upgrade a listing to unlock these premium features:";

  return (
    <div className="overflow-hidden rounded-xl border border-dashed border-accent-300 bg-accent-50/60">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-accent-200/70 px-6 py-4">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
            <i className="ri-lock-2-line text-lg" />
          </span>
          <div>
            <h2 className="font-heading text-base font-bold text-foreground-950">Premium features</h2>
            <p className="mt-0.5 text-xs text-foreground-600">{subtitle}</p>
          </div>
        </div>
        <Link
          to="/listing-plans"
          className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-rocket-2-line text-sm" /> Upgrade plan
        </Link>
      </div>

      <ul className="grid grid-cols-1 divide-y divide-accent-200/60 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        {UNLOCKS.map((item) => (
          <li key={item.title} className="p-5">
            <div className="flex items-center justify-between gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                <i className={`${item.icon} text-base`} />
              </span>
              <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-secondary-900">
                {item.plans}
              </span>
            </div>
            <h3 className="mt-3 text-sm font-bold text-foreground-950">{item.title}</h3>
            <p className="mt-1 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}